import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Clock, Search } from "lucide-react";
import { api } from "../lib/api";
import { applySeo } from "../lib/seoHead";
import { useSiteLayout } from "../site/useSiteLayout";
import SiteBanners from "../components/SiteBanners";
import { PageLoader } from "../components/LoadingRing";

const dateLabel = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

const Card = ({ post, featured = false }) => (
    <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}
        className={`group border border-white/10 bg-bg1 hover:border-gold/40 overflow-hidden transition-all hover:-translate-y-1 ${
            featured ? "md:col-span-2 md:grid md:grid-cols-2" : ""}`}>
        <div className={`relative overflow-hidden bg-bg0 ${featured ? "h-64 md:h-full min-h-[240px]" : "h-48"}`}>
            {post.featured_image?.url ? (
                <img src={post.featured_image.url} alt={post.featured_image.alt || post.title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            ) : <div className="w-full h-full bg-gradient-to-br from-lime/25 to-bg2" />}
            {post.category && (
                <div className="absolute top-4 left-4 bg-black/70 backdrop-blur border border-white/10 px-2.5 py-1
                                mono text-[9px] tracking-[0.18em] text-gold">{post.category.name}</div>
            )}
        </div>
        <div className={`p-6 ${featured ? "flex flex-col justify-center" : ""}`}>
            <h3 className={`font-display uppercase tracking-tight ${featured ? "text-2xl md:text-3xl" : "text-xl"}`}>{post.title}</h3>
            <p className="mt-3 text-zinc-400 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
            <div className="mt-5 flex items-center justify-between gap-3 mono text-[10px] tracking-[0.16em] text-zinc-500">
                <span className="truncate">{post.author?.name || "ReHealthMe"} · {dateLabel(post.published_at)}</span>
                <span className="inline-flex items-center gap-1.5 shrink-0">
                    <Clock className="w-3.5 h-3.5" /> {post.reading_minutes} MIN
                </span>
            </div>
        </div>
    </Link>
);

export default function Blog({ embedded = false, heading = "", sub = "" }) {
    const { categorySlug } = useParams();
    const [params, setParams] = useSearchParams();
    const q = params.get("q") || "";
    const [term, setTerm] = React.useState(q);

    React.useEffect(() => {
        const id = setTimeout(() => {
            if (term !== q) setParams(term ? { q: term } : {}, { replace: true });
        }, 400);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [term]);

    const { page: hubPage } = useSiteLayout("blog");
    const { data: meta } = useQuery({ queryKey: ["blog-meta"], queryFn: async () => (await api.get("/blog/meta")).data });
    const { data: posts = [], isLoading } = useQuery({
        queryKey: ["blog-posts", categorySlug || "", q],
        queryFn: async () => (await api.get("/blog/posts", {
            params: { category: categorySlug || undefined, q: q || undefined, limit: 30 },
        })).data,
    });

    const activeCategory = (meta?.categories || []).find((c) => c.slug === categorySlug);

    React.useEffect(() => {
        const pageSeo = hubPage?.seo || {};
        applySeo({
            title: activeCategory
                ? `${activeCategory.name} Articles — ReHealthMe`
                : pageSeo.seo_title || hubPage?.title || "ReHealthMe Journal — Health & Wellness Articles",
            description: activeCategory?.description || (categorySlug ? "" : pageSeo.meta_description)
                || meta?.settings?.blog_intro || "",
            canonical: `${window.location.origin}/blog${categorySlug ? `/category/${categorySlug}` : ""}`,
        });
    }, [activeCategory, categorySlug, meta, hubPage]);

    const submit = (e) => {
        e.preventDefault();
        setParams(term ? { q: term } : {});
    };

    return (
        <div data-testid="blog-page">
            <SiteBanners placement="blog" />
            <div className={`${embedded ? "pt-10" : "pt-24 md:pt-28"} pb-20`}>
                <div className="mx-auto max-w-7xl px-6 md:px-12">
                    <div className="mb-10">
                        <div className="eyebrow mb-3">Journal</div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
                            {activeCategory ? activeCategory.name : (heading || "ReHealthMe Journal")}
                        </h1>
                        <p className="mt-4 text-zinc-400 max-w-2xl text-base">
                            {activeCategory?.description || sub || meta?.settings?.blog_intro}
                        </p>
                    </div>

                    <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-10">
                        <form onSubmit={submit} className="relative w-full lg:max-w-sm" data-testid="blog-search-form">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                            <input value={term} onChange={(e) => setTerm(e.target.value)} data-testid="blog-search-input"
                                placeholder="Search articles as you type"
                                className="w-full bg-bg1 border border-white/12 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-gold" />
                        </form>
                        <div className="flex flex-wrap gap-2" data-testid="blog-category-filters">
                            <Link to="/blog" data-testid="blog-filter-all"
                                className={`px-3 py-1.5 border mono text-[10px] tracking-[0.16em] transition-colors ${
                                    !categorySlug ? "border-gold text-gold" : "border-white/12 text-zinc-400 hover:text-white"}`}>ALL</Link>
                            {(meta?.categories || []).filter((c) => c.post_count > 0).map((c) => (
                                <Link key={c.id} to={`/blog/category/${c.slug}`} data-testid={`blog-filter-${c.slug}`}
                                    className={`px-3 py-1.5 border mono text-[10px] tracking-[0.16em] transition-colors ${
                                        categorySlug === c.slug ? "border-gold text-gold" : "border-white/12 text-zinc-400 hover:text-white"}`}>
                                    {c.name.toUpperCase()}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {isLoading ? (
                        <PageLoader label="LOADING ARTICLES…" className="py-20" />
                    ) : posts.length === 0 ? (
                        <div className="border border-white/10 bg-bg1 p-12 text-center" data-testid="blog-empty">
                            <div className="font-display text-2xl uppercase tracking-tight">No articles yet</div>
                            <p className="text-zinc-400 mt-2 text-sm">
                                {q ? "Nothing matched your search. Try a different word." : "New articles are on the way."}
                            </p>
                            {q && <Link to="/blog" className="mt-4 inline-flex items-center gap-2 mono text-[10px] tracking-[0.16em] text-gold">
                                CLEAR SEARCH <ArrowRight className="w-3.5 h-3.5" /></Link>}
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
                            {posts.map((p, i) => <Card key={p.id} post={p} featured={i === 0 && !q && posts.length > 2} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
